import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, queryAll, state } from 'lit/decorators.js';
import 'src/library/elements/d-label';
import 'src/library/editors/elements/d-select-checkbox';
import 'src/library/elements/d-smooth-resize';
import type { EmployeePresenceStatus } from 'src/store/selectors/staffing/employee-presence-status';
import { classMap } from 'lit/directives/class-map.js';
import type { EmployeesSlide } from 'src/content/screens/slide-element';
import { t } from 'src/i18n';
const PROFESSION_DOCTOR = 'Lege';

/**
 *
 */
@customElement('d-employees-slide')
export class DEmployeesSlide extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      box-sizing: border-box;
      aspect-ratio: 16/9;
      container-type: inline-size;
    }
    .wrapper {
      display: flex;
      flex-direction: column;
      position: relative;
      box-sizing: border-box;
      aspect-ratio: 16/9;
      padding: 0 4cqi 2cqi 4cqi;
      background: white;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding-top: 4cqi;
      border-bottom: 0.2cqi solid var(--borderColor);
      padding-bottom: 1cqi;
      background: white;
    }
    .title {
      font-family: var(--screen-slide-header-font), sans-serif;
      font-weight: 600;
      font-size: 3.9cqi;
      line-height: 106%;
    }
    .legends {
      display: flex;
      gap: 1cqi;
    }
    .content {
      position: relative;
      display: flex;
      justify-content: flex-start;
      gap: 2cqi;
      padding-top: 2cqi;
      max-height: 42cqi;
      overflow: hidden;
    }
    .list-wrapper {
      position: relative;
    }
    .list-wrapper:first-child {
      flex: 40%;
    }
    .list-wrapper:last-child {
      flex: 60%;
      padding-left: 2cqi;
      border-left: 0.2cqi solid var(--borderColor);
    }
    .content > div:last-child.single {
      margin-top: -2cqi;
      border: none;
    }
    .label {
      font-family: var(--screen-slide-header-font), sans-serif;
      font-weight: 600;
      font-size: 3.2cqi;
    }
    .item {
      padding-left: 4.2cqi;
      background: url(/images/x-fat-orange.svg) 0 -0.2cqw no-repeat;
      background-size: 4cqw auto;
      font-family: var(--screen-slide-text-font), serif;
      font-weight: 400;
      font-size: 2.7cqi;
      line-height: 130%;
    }
    .legends .item {
      padding-top: 0.5cqi;
      padding-bottom: 0.5cqi;
      font-family: var(--screen-slide-header-font), sans-serif;
      font-size: 2cqi;
    }
    .list-wrapper {
      display: flex;
      flex-direction: column;
    }
    .list {
      position: relative;
      margin-left: 1cqi;
      overflow: hidden;
    }
    .list-content {
      position: relative;
      top: -1px;
      padding-top: 2cqi;
      padding-bottom: 0.5cqi;
    }
    .overflow-gradient {
      position: absolute;
      bottom: -1px;
      width: 100%;
      height: 3cqi;
      background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) 10%,
        rgba(255, 255, 255, 0) 100%
      );
    }
    .overflow-gradient.top {
      bottom: unset;
      top: -1px;
      transform: rotate(180deg);
      z-index: 1;
    }
    .list .item {
      margin-bottom: 0.8cqi;
      background-position: 0 -0.2cqw;
    }
    .list .item.hide {
      display: none;
    }
    .item.present {
      background-image: url(/images/check-mini-blue.svg);
      background-size: 4cqw auto;
    }
    .info {
      position: absolute;
      bottom: 10px;
      left: 10px;
    }
    .controllers-position {
      position: absolute;
      width: calc(100% - 40px);
      bottom: 20px;
      right: 20px;
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
    }
    .controllers {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: stretch;
      border-radius: 16px;
      box-shadow: 0 2px 10px hsla(1, 0%, 0%, 0.3);
      background: white;
      padding: 12px;
    }
    .controllers-bottom {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }
    d-label {
      display: block;
      margin: 4px 0;
    }
    d-select-checkbox {
      box-sizing: border-box;
      position: relative;
      border-bottom: 1px solid var(--borderColor);
      margin-bottom: 10px;
      padding-bottom: 12px;
      max-width: calc(100cqi - 40px);
      max-height: calc(55cqi - 126px);
      overflow: auto;
      z-index: 1;
    }
    button {
      -moz-appearance: none;
      -webkit-appearance: none;
      box-sizing: border-box;
      height: 30px;
      border: 1px solid var(--borderColor);
      border-radius: 6px;
      background: white;
      font-family: var(--mainSans);
      font-size: 15px;
      text-align: center;
      cursor: pointer;
      outline: none;
    }
  `;
  @property({ type: Object })
  slide!: EmployeesSlide;
  @property({ type: Array })
  employees: EmployeePresenceStatus[] = [];
  @property({ type: Boolean })
  edit = false;
  @property({ type: Boolean })
  internalEdit = false;
  @queryAll('.list-wrapper')
  listWrappers!: NodeListOf<HTMLElement>;

  @state() private doctors: EmployeePresenceStatus[] = [];
  @state() private others: EmployeePresenceStatus[] = [];

  private get hasDoctors(): boolean {
    return this.doctors.length > 0;
  }

  private get displayedDoctors(): EmployeePresenceStatus[] {
    if (this.slide.doctorsSeparately) {
      return this.doctors.filter((e) => !this.slide.hide.includes(e.uuid));
    }
    return [];
  }

  private get displayedOthers(): EmployeePresenceStatus[] {
    if (this.slide.doctorsSeparately) {
      return this.others.filter((e) => !this.slide.hide.includes(e.uuid));
    }
    return this.employees.filter((e) => !this.slide.hide.includes(e.uuid));
  }

  private get selected() {
    return this.employees
      .filter((e) => {
        return !this.slide.hide.includes(e.uuid);
      })
      .map((e) => e.uuid);
  }

  private fireChange() {
    this.dispatchEvent(
      new CustomEvent<{ value: EmployeesSlide }>('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.slide },
      }),
    );
  }

  private onselectEmployees(e: CustomEvent<{ value: string[] }>) {
    e.stopPropagation();
    this.slide = {
      ...this.slide,
      hide: this.employees.filter((emp) => !e.detail.value.includes(emp.uuid)).map((emp) => emp.uuid),
    };
    this.fireChange();
  }

  private onDoctorsSeparatelyChanged(e: CustomEvent<{ checked: boolean }>) {
    this.slide = { ...this.slide, doctorsSeparately: e.detail.checked };
    this.fireChange();
  }

  private itemClass(e: EmployeePresenceStatus) {
    return {
      item: true,
      present: e.present,
    };
  }

  private scrollLists() {
    this.listWrappers.forEach((listWrapper: HTMLElement) => {
      const list = listWrapper.querySelector('.list') as HTMLElement;
      const content = listWrapper.querySelector('.list-content') as HTMLElement;
      content.removeAttribute('style');
      const gradient = listWrapper.querySelector('.overflow-gradient') as HTMLElement;
      if (!list || !content || !gradient) return;
      const listHeight = list.offsetHeight;
      const contentHeight = content.offsetHeight;
      const gradientHeight = gradient.offsetHeight;
      if (contentHeight > listHeight) {
        content.style.transition = 'top ' + (this.slide.duration - 2) + 's';
        setTimeout(() => {
          content.style.top = (contentHeight - listHeight + gradientHeight) * -1 + 'px';
        }, 1000);
      }
    });
  }

  protected willUpdate(changed: PropertyValues) {
    if (changed.has('employees')) {
      this.doctors = this.employees.filter((e) => e.profession === PROFESSION_DOCTOR);
      this.others = this.employees.filter((e) => e.profession !== PROFESSION_DOCTOR);
    }
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.scrollLists();
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="header">
          <div class="title">${t('screens.suggestionsSlideEmployees')}</div>
          <div class="legends">
            <div class="item present">${t('screens.employeesSlide.present')}</div>
            <div class="item">${t('screens.employeesSlide.notPresent')}</div>
          </div>
        </div>
        <div class="content">
          ${this.displayedDoctors.length
            ? html`
                <div class="list-wrapper">
                  <div class="label">${t('screens.employeesSlide.doctors')}</div>
                  <div class="list">
                    <div class="overflow-gradient top"></div>
                    <div class="list-content">
                      ${this.displayedDoctors.map((e) => {
                        return html`<div class=${classMap(this.itemClass(e))}>
                          <span>${e.name}</span>
                        </div>`;
                      })}
                    </div>
                  </div>
                  <div class="overflow-gradient"></div>
                </div>
              `
            : nothing}
          ${this.displayedOthers.length
            ? html`
                <div class="list-wrapper ${!this.displayedDoctors.length ? 'single' : ''}">
                  ${this.displayedDoctors.length
                    ? html` <div class="label">${t('screens.employeesSlide.otherStaff')}</div> `
                    : nothing}
                  <div class="list">
                    <div class="overflow-gradient top"></div>
                    <div class="list-content">
                      ${this.displayedOthers.map((e) => {
                        return html`<div class=${classMap(this.itemClass(e))}>
                          <span>${e.name}</span>${e.profession ? html`, <span>${e.profession}</span>` : nothing}
                        </div>`;
                      })}
                    </div>
                  </div>
                  <div class="overflow-gradient"></div>
                </div>
              `
            : nothing}
        </div>
        ${this.edit ? html` <div class="info">${t('screens.employeesSlide.basedOnStaffingCalendar')}</div>` : nothing}
        ${this.edit
          ? html`
              <div class="controllers-position">
                <div class="controllers">
                  <d-smooth-resize>
                    ${this.internalEdit
                      ? html`
                          <d-label big label=${t('screens.employeesSlide.showHideStaff')}></d-label>
                          <d-select-checkbox
                            checklist
                            .options=${this.employees.map((e) => ({ value: e.uuid, text: e.name }))}
                            .value=${this.selected}
                            @value-changed=${(e: CustomEvent<{ value: string[] }>) => this.onselectEmployees(e)}
                          ></d-select-checkbox>
                        `
                      : nothing}
                  </d-smooth-resize>
                  <div class="controllers-bottom">
                    <button
                      @click=${() => {
                        this.internalEdit = !this.internalEdit;
                        if (!this.internalEdit) {
                          this.scrollLists();
                        }
                      }}
                    >
                      ${this.internalEdit ? t('screens.done') : t('screens.employeesSlide.edit')}
                    </button>
                    ${this.internalEdit && this.hasDoctors
                      ? html`<d-checkbox
                          controller
                          .optionLabel=${t('screens.employeesSlide.showDoctorsSeparately')}
                          .checked=${this.slide.doctorsSeparately}
                          @checked-changed=${(e: CustomEvent<{ checked: boolean }>) =>
                            this.onDoctorsSeparatelyChanged(e)}
                        ></d-checkbox>`
                      : nothing}
                  </div>
                </div>
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-employees-slide': DEmployeesSlide;
  }
}
